// Copyright TraderEvolution Global LTD. © 2017-2024. All rights reserved.

import { ErrorInformationStorage } from "../../../Commons/ErrorInformationStorage.js";
import { TerceraChart } from "../../../Chart/TerceraChart.js";
import { ControlsUtils } from "../../UtilsClasses/ControlsUtils.js";
import { TerceraChartRactiveTemplate } from "../../../templates.js";
import { Resources } from "../../../Commons/properties/Resources.js";
import { Control } from "../Control.js";
import { ThemeManager } from "../../misc/ThemeManager.js";
import { KeyEventProcessor } from "../../../Commons/KeyEventProcessor.js";
import { DynProperty } from "../../../Commons/DynProperty.js";
import { RactiveTooltip } from '../../misc/Decorators.js';

export let TerceraChartRactive = Control.extend({
    template: TerceraChartRactiveTemplate,
    data: function ()
    {
        return {
            //backgroundColorTop: 'purple',
            backgroundColorBottom: 'rgb(16, 26, 36)',
            currentCursor: 'default',
            terceraChartPanelContext: null,
            useStyle: true,
            width: 0,
            height: 0
        };
    },
    terceraChart: null,
    tooltipTimeout: null
});

TerceraChartRactive.prototype.getType = function () { return 'TerceraChartRactive' };

// Static field
TerceraChartRactive.staticID = 0;

TerceraChartRactive.prototype.oninit = function ()
{
    TerceraChartRactive.staticID++;
    this.set('id', 'chart' + TerceraChartRactive.staticID);

    KeyEventProcessor.OnKeyDown.Subscribe(this.onGlobalKeyDown, this);
};

TerceraChartRactive.prototype.oncomplete = function ()
{
    Control.prototype.oncomplete.apply(this);
    try
    {
        var $canvas = $('#' + this.get('id'));

        this.terceraChart = new TerceraChart(
            $canvas[0].getContext('2d'),
            this.get('terceraChartPanelContext'));
        this.terceraChart.OnUpdateTooltip.Subscribe(this.onUpdateTooltip, this);

        this.terceraChart.ThemeChanged();

        this.observe('width', this.onWidthChanged);
        this.observe('height', this.onHeightChanged);
    }
    catch (ex) 
    {
        ErrorInformationStorage.GetException(ex);
        console.log('chart oncomplete exception: ' + ex);
    }
};

TerceraChartRactive.prototype.onteardown = function ()
{
  this.terceraChart.OnUpdateTooltip.UnSubscribe(this.onUpdateTooltip, this);
    KeyEventProcessor.OnKeyDown.UnSubscribe(this.onGlobalKeyDown, this);
};

TerceraChartRactive.prototype.onUpdateTooltip = function (tooltip) {
  if (this.tooltipTimeout) { clearTimeout(this.tooltipTimeout); }

  this.tooltipTimeout = null;

  if (!tooltip) {
    RactiveTooltip.setTooltipVisibility(false);
    return;
  }

  this.tooltipTimeout = setTimeout(function (tooltip) {
    RactiveTooltip.showTooltip(tooltip);
  }, 500, tooltip);
};
TerceraChartRactive.prototype.onResizeBegin = function () { };
TerceraChartRactive.prototype.onResizeEnd = function () { };

TerceraChartRactive.prototype.onWidthChanged = function (newWidth)
{
    this.terceraChart.width = newWidth;
};

TerceraChartRactive.prototype.onHeightChanged = function (newHeight)
{
    this.terceraChart.height = newHeight;
};

TerceraChartRactive.prototype.setCursor = function (cursor)
{
    this.set('currentCursor', cursor || 'default');
};

TerceraChartRactive.prototype.onMouseDown = function (event)
{
    Control.prototype.onMouseDown.call(this, event);
    var newChartEvent = this.terceraChart.ImproveArgs(event.original || event);
    this.terceraChart.ProcessMouseDown(newChartEvent);
    ControlsUtils.Capture.SetCapture(this, event.original);
};

TerceraChartRactive.prototype.onMouseMove = function (event)
{
    if (this.captured && event.original)
        return;
    Control.prototype.onMouseMove.call(this, event);
    var newChartEvent = this.terceraChart.ImproveArgs(event.original || event);
    this.terceraChart.ProcessMouseMove(newChartEvent);
};

TerceraChartRactive.prototype.onMouseUp = function (event)
{
    if (this.captured && event.original)
        return;
    Control.prototype.onMouseUp.call(this, event);
    var newChartEvent = this.terceraChart.ImproveArgs(event.original || event);
    this.terceraChart.ProcessMouseUp(newChartEvent);
};

TerceraChartRactive.prototype.onMouseWheel = function (event)
{
    Control.prototype.onMouseWheel.call(this, event);
    var newChartEvent = this.terceraChart.ImproveArgs(event.original || event);
    this.terceraChart.ProcessMouseWheel(newChartEvent);
};

TerceraChartRactive.prototype.onGlobalKeyDown = function ()
{
    var container = this.get('parentContainerControl');
    if (container && container.get('focused') || this.get('focused'))
        this.terceraChart.OnKeyDown();
};

TerceraChartRactive.prototype.onMouseEnter = function (event)
{
    Control.prototype.onMouseEnter.call(this, event);
    this.terceraChart.ProcessMouseEnter(event);
};

TerceraChartRactive.prototype.onMouseLeave = function (event)
{
    Control.prototype.onMouseLeave.call(this, event);
    this.terceraChart.ProcessMouseLeave(event);
};

TerceraChartRactive.prototype.onDoubleClick = function (event)
{
    Control.prototype.onDoubleClick.call(this, event);
    var newChartEvent = this.terceraChart.ImproveArgs(event.original || event);
    this.terceraChart.ProcessMouseDoubleClick(newChartEvent);
};

TerceraChartRactive.prototype.onClick = function (event)
{
    Control.prototype.onClick.call(this, event);
    this.terceraChart.ProcessMouseClick(event);
};

TerceraChartRactive.prototype.themeChange = function ()
{
    //this.set('backgroundColorTop', App.ThemeManager.CurrentTheme.Chart_BottomColor);
    this.set('backgroundColorBottom', ThemeManager.CurrentTheme.Chart_BottomColor);

    if (this.terceraChart)
        this.terceraChart.ThemeChanged();
};

TerceraChartRactive.prototype.localize = function ()
{
    if (this.terceraChart)
        this.terceraChart.Localize();
};

//#region ICaller

TerceraChartRactive.prototype.Properties = function ()
{
    var properties = this.terceraChart.Properties();

    //Window colors
    var SeparatorGroup = "#-1#" + Resources.getResource("property.SeparatorGroup.WindowColors");

    /*var prop = new DynProperty("Backgr. top", this.get('backgroundColorTop'), DynProperty.COLOR, DynProperty.VISUAL_GROUP);
    prop.separatorGroup = SeparatorGroup;
    prop.sortIndex = 0;
    properties.push(prop);*/

    var prop = new DynProperty("property.Backgr", this.get('backgroundColorBottom'), DynProperty.COLOR, DynProperty.VISUAL_GROUP);
    prop.separatorGroup = SeparatorGroup;
    prop.sortIndex = 1;
    properties.push(prop);

    return properties;
};

TerceraChartRactive.prototype.callBack = function (properties)
{
    this.terceraChart.callBack(properties);

    /*dp = DynProperty.getPropertyByName(properties, "Backgr. top");
    if (dp) this.set('backgroundColorTop', dp.value);*/

    var dp = DynProperty.getPropertyByName(properties, "property.Backgr");
    if (dp) this.set('backgroundColorBottom', dp.value);
};

//#endregion